import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import { Paginator } from 'primereact/paginator'
import Button from '../../components/ui/Button'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import FilterBar from '../../components/ui/FilterBar'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import { useToast } from '../../components/ui/ToastProvider'
import { useGrants } from '../../auth/useCurrentUser'
import { GRANTS } from '../../auth/grants'
import { useProxyMode } from '../proxy/useProxyMode'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import { getApiErrorCode, isOptimisticLock } from '../../api/apiError'
import { exportMeetingGroups } from './api/meetingApi'
import { meetingGroupColumns } from './components/MeetingGroupColumns'
import MeetingGroupFormDialog from './components/MeetingGroupFormDialog'
import MeetingGroupRowActions from './components/MeetingGroupRowActions'
import { useMeetingGroups } from './hooks/useMeetingGroups'
import type { MeetingGroup, MeetingGroupUpsert } from './types'
import '../admin/admin.css'
import './meetings.css'

const PAGE_SIZE = 20

/**
 * Tutanak Takip → **Toplantı Grupları** yönetim ekranı (041). Grupların **CRUD**'u burada yapılır (ekle/
 * düzenle/sil); "Toplantılar" ekranı artık salt-okuma drill-down'dır. Altında **aktif toplantı** bulunan grup
 * **silinemez** (backend 409 → kullanıcıya mesaj). Başlık = menü adı. Yetki: MEETING_GROUP_MANAGE.
 */
export default function MeetingGroupsPage() {
  const { t } = useTranslation()
  const toast = useToast()
  // 077 — Grup No kolonu yalnız RECORD_NUMBER_VIEW yetkisinde (Özel Kalem).
  const showRecordNumber = useGrants().hasGrant(GRANTS.RECORD_NUMBER_VIEW)
  // 126 — Kısıtlı Yetkili asistan ekranı SALT OKUNUR görür: Yeni/düzenle/sil hiç çizilmez.
  const { readOnly } = useProxyMode()

  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [page, setPage] = useState(0)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<MeetingGroup | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)

  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(search)
      setPage(0)
    }, 300)
    return () => clearTimeout(id)
  }, [search])

  const groups = useMeetingGroups({ q: debounced, page, size: PAGE_SIZE })
  const items = groups.list.data?.content ?? []
  const total = groups.list.data?.page.totalElements ?? 0
  // Dar ekranda (eşik: CARD_VIEW_BREAKPOINT — lib/useMediaQuery) tablo yerine kart listesi — aynı veri/işlemler (bkz. meetings.css
  // `.mgl-card-list` — ui.css `.ui-card-list` ailesinden bu ekrana özgü ayrıştırılmış kopya). Sayfalama
  // bu sayfada zaten DataTable DIŞINDA (aşağıdaki `<Paginator>`), bu yüzden her iki dalda da aynen kullanılır.
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  const openNew = () => {
    setEditing(null)
    setNameError(null)
    setFormOpen(true)
  }
  const openEdit = (g: MeetingGroup) => {
    setEditing(g)
    setNameError(null)
    setFormOpen(true)
  }

  const handleFormError = (err: unknown) => {
    if (getApiErrorCode(err) === 'DUPLICATE_NAME') {
      setNameError(t('meetings.errors.duplicate'))
      return
    }
    if (isOptimisticLock(err)) {
      setFormOpen(false)
      groups.list.refetch()
    }
  }

  const submit = (payload: MeetingGroupUpsert) => {
    if (editing) {
      groups.update.mutate(
        { id: editing.id, body: { ...payload, modDate: editing.modDate } },
        {
          onSuccess: () => {
            toast.success(t('meetings.toast.updated'))
            setFormOpen(false)
          },
          onError: handleFormError,
        },
      )
    } else {
      groups.create.mutate(payload, {
        onSuccess: () => {
          toast.success(t('meetings.toast.created'))
          setFormOpen(false)
        },
        onError: handleFormError,
      })
    }
  }

  const del = (g: MeetingGroup) => {
    confirmDialog({
      header: t('meetings.delete.title'),
      message: t('meetings.delete.confirm', { name: g.name }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('meetings.delete.accept'),
      accept: () => {
        setBusyId(g.id)
        groups.remove.mutate(g.id, {
          onSuccess: () => toast.success(t('meetings.toast.deleted')),
          onError: (err) => {
            // Altında aktif toplantı varsa silme engellenir (409) → alan-dışı toast mesajı.
            if (getApiErrorCode(err) === 'MEETING_GROUP_IN_USE') {
              toast.error(t('meetings.errors.inUse'))
            }
          },
          onSettled: () => setBusyId(null),
        })
      },
    })
  }

  return (
    <div className="admin-page">
      <ConfirmDialog />
      <MeetingGroupFormDialog
        visible={formOpen}
        initial={editing}
        submitting={groups.create.isPending || groups.update.isPending}
        nameError={nameError}
        onClearNameError={() => setNameError(null)}
        onSubmit={submit}
        onHide={() => setFormOpen(false)}
      />

      <PageHeader
        title={t('nav.tutanak.meetingGroups')}
        actions={
          <>
            {/* Yenile, Excel'in SOLUNDA (kullanıcı isteği 2026-08-27); önceden filtre çubuğundaydı. */}
            <RefreshButton onRefresh={() => groups.list.refetch()} />
            <ExcelExportButton
              onExport={() => exportMeetingGroups(debounced)}
              disabled={items.length === 0}
            />
            {!readOnly && (
              <Button icon="pi pi-plus" label={t('meetings.actions.new')} onClick={openNew} />
            )}
          </>
        }
      />
      <FilterBar
        searchValue={search}
        searchPlaceholder={t('meetings.search')}
        onSearch={setSearch}
      />
      {isMobile ? (
        <div className="mgl-card-list">
          {groups.list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="mgl-card mgl-card--skeleton" aria-hidden />
            ))}
          {!groups.list.isLoading && items.length === 0 && (
            <EmptyState
              icon="pi pi-users"
              title={t('meetings.empty.title')}
              description={t('meetingGroups.empty.desc')}
            />
          )}
          {!groups.list.isLoading &&
            items.map((row) => (
              <div key={row.id} className="mgl-card">
                <div className="mgl-card-head">
                  <span className="mgl-card-title">{row.name}</span>
                  {/* Menü yalnız yazma işlemleri taşır → salt okunur kipte hiç çizilmez. */}
                  {!readOnly && (
                    <div className="mgl-card-actions">
                      <MeetingGroupRowActions
                        row={row}
                        onEdit={openEdit}
                        onDelete={del}
                        busy={busyId === row.id}
                      />
                    </div>
                  )}
                </div>

                {row.description && <p className="mgl-card-row">{row.description}</p>}
                <div className="mgl-card-row">
                  <span>
                    {t('meetingGroups.columns.meetingCount')}: {row.meetingCount}
                  </span>
                </div>
              </div>
            ))}
        </div>
      ) : (
        <DataTable
          value={items}
          loading={groups.list.isLoading}
          paginator={false}
          emptyMessage={
            <EmptyState
              icon="pi pi-users"
              title={t('meetings.empty.title')}
              description={t('meetingGroups.empty.desc')}
            />
          }
        >
          {meetingGroupColumns({
            t,
            busyId,
            onEdit: openEdit,
            onDelete: del,
            showRecordNumber,
            readOnly,
          })}
        </DataTable>
      )}
      {total > PAGE_SIZE && (
        <Paginator
          first={page * PAGE_SIZE}
          rows={PAGE_SIZE}
          totalRecords={total}
          onPageChange={(e) => setPage(e.page)}
        />
      )}
    </div>
  )
}
